import type { UnitId } from "./content/buildings.js";
import { UNIT_IDS } from "./logistics-types.js";
import { exactRecordKeys, recordObject } from "./record-validation.js";
import type { ReinforcementIntent } from "./reinforcement-commands.js";
import { validateShares } from "./reinforcement-state.js";
import type { ConflictIntent } from "./types.js";
import { validateWaveSeconds } from "./wave-timing.js";

export function decodeReinforcementIntent(
  source: Record<string, unknown>,
): ReinforcementIntent | Extract<ConflictIntent, { kind: "allocate-army" | "adjust-army" }> {
  if (source.kind === "adjust-army") return decodeArmyAdjustment(source);
  if (source.kind === "allocate-army") {
    exactRecordKeys(source, ["kind", "shares", ...(source.wave === undefined ? [] : ["wave"])]);
    const shares = recordObject(source.shares, "army shares");
    const wave = source.wave === undefined ? undefined : recordObject(source.wave, "wave");
    if (wave) {
      exactRecordKeys(wave, ["borderId", "seconds"]);
      if (typeof wave.borderId !== "string" || !wave.borderId || wave.borderId.length > 128)
        throw new TypeError("Invalid border ID");
    }
    return {
      kind: "allocate-army",
      shares: validateShares(shares, Object.keys(shares)),
      ...(wave
        ? {
            wave: { borderId: wave.borderId as string, seconds: validateWaveSeconds(wave.seconds) },
          }
        : {}),
    };
  }
  if (source.kind === "route-production") {
    exactRecordKeys(source, ["kind", "unit", "shares"]);
    if (source.unit !== "all" && !UNIT_IDS.includes(source.unit as never))
      throw new TypeError("Unknown troop type");
    const shares = recordObject(source.shares, "production shares");
    return {
      kind: source.kind,
      unit: source.unit as UnitId | "all",
      shares: validateShares(shares, Object.keys(shares)),
    };
  }
  exactRecordKeys(
    source,
    source.kind === "set-dispatch-mode"
      ? ["kind", "borderId", "mode"]
      : source.kind === "set-wave-interval"
        ? ["kind", "borderId", "seconds"]
        : ["kind", "borderId"],
  );
  if (
    typeof source.borderId !== "string" ||
    !source.borderId.length ||
    source.borderId.length > 128
  )
    throw new TypeError("Invalid border ID");
  if (source.kind === "set-wave-interval")
    return {
      kind: source.kind,
      borderId: source.borderId,
      seconds: validateWaveSeconds(source.seconds),
    };
  if (source.kind === "set-dispatch-mode") {
    if (source.mode !== "gather" && source.mode !== "continuous")
      throw new TypeError("Invalid dispatch mode");
    return { kind: source.kind, borderId: source.borderId, mode: source.mode };
  }
  if (source.kind !== "launch-wave" && source.kind !== "release-wave")
    throw new TypeError("Unknown reinforcement command");
  return { kind: source.kind, borderId: source.borderId };
}

function decodeArmyAdjustment(
  source: Record<string, unknown>,
): Extract<ConflictIntent, { kind: "adjust-army" }> {
  exactRecordKeys(source, ["kind", "borderId", "field", "value"]);
  if (typeof source.borderId !== "string" || !source.borderId || source.borderId.length > 128)
    throw new TypeError("Invalid border ID");
  if (source.field !== "share" && source.field !== "seconds")
    throw new TypeError("Unknown army control");
  if (
    !Number.isInteger(source.value) ||
    (source.value as number) < 0 ||
    (source.value as number) > 120
  )
    throw new TypeError("Invalid army value");
  if (source.field === "seconds") validateWaveSeconds(source.value);
  else if ((source.value as number) > 100) throw new TypeError("Invalid army share");
  return {
    kind: "adjust-army",
    borderId: source.borderId,
    field: source.field,
    value: source.value as number,
  };
}
